import {
  OrderedListOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Button, Modal, Tooltip } from 'antd'
import classNames from 'classnames'
import { useState } from 'react'
import { toast } from 'sonner'

import { segmentsQueries } from '@/api/segments/segmentsQueries'
import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import AtomSearchInput from '@/components/Atoms/AtomSearchInput/AtomSearchInput'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import AtomTable from '@/components/Atoms/AtomTable/AtomTable'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import Loader from '@/components/Atoms/Loader/Loader'
import SegmentCreationModal from '@/components/EntityCreationModals/SegmentCreationModal/SegmentCreationModal'
import type { SegmentModalMode } from '@/components/EntityCreationModals/SegmentCreationModal/SegmentCreationModal.config'
import { useChangeSegmentStatusMutation } from '@/hooks/segments/useChangeSegmentStatusMutation'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { SegmentModel, SegmentStatusType } from '@/types'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { hasRoleFromGroup } from '@/utils/auth/roleGroups'

import SegmentsOrderModal from './SegmentsOrderModal/SegmentsOrderModal'
import {
  getTanstackColumns,
  SEGMENT_OWNER_OPTIONS,
  type SegmentOwner,
  SEGMENTS_COLUMN_WIDTHS,
} from './SegmentsTab.config'
import cn from './SegmentsTab.module.scss'

const ORDER_HINT =
  'Порядок сегментов от самого дешёвого к самому дорогому. По нему менеджеры и продавцы в поиске видят цены только по соседним сегментам — на один дешевле и на один дороже сегмента клиента. Администраторы, суперменеджеры и менеджеры по остаткам видят все сегменты.'

const EMPTY_SEGMENTS: SegmentModel[] = []

function SegmentsTab() {
  const [innerTab, setInnerTab] = useState<SegmentOwner>('my')
  const [search, setSearch] = useState('')
  const [modalMode, setModalMode] = useState<SegmentModalMode | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalKey, setModalKey] = useState(0)
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false)
  const canReorder = useUserInfoStore((state) =>
    hasRoleFromGroup(state.tokenRoles, 'SENIOR_MANAGERS')
  )

  const isOrgSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )
  const [modal, contextHolder] = Modal.useModal()

  const segmentsQuery = useQuery(
    segmentsQueries.list(innerTab === 'my' ? 'mine' : 'shared-with-me')
  )
  const changeStatus = useChangeSegmentStatusMutation()

  const handleStatusChange = (id: string, status: SegmentStatusType) => {
    const toastId = toast.loading('Смена статуса...')
    changeStatus.mutate(
      { id, status },
      {
        onSuccess: () => {
          toast.success('Статус успешно изменен', { id: toastId })
        },
        onError: (error) => {
          toast.error('Ошибка при смене статуса', { id: toastId })
          handleApiError(error, modal)
        },
      }
    )
  }

  const openModal = (mode: SegmentModalMode) => {
    setModalMode(mode)
    setModalKey((key) => key + 1)
    setIsModalOpen(true)
  }

  const columns = getTanstackColumns({
    innerTab,
    onEdit: (segment) => openModal({ type: 'edit', segment }),
    onChangeStatus: handleStatusChange,
    onCreateBasedOn: (segment) => {
      setInnerTab('my')
      openModal({ type: 'create', baseSegment: segment })
    },
  })

  const normalizedSearch = search.trim().toLowerCase()
  const segments = (segmentsQuery.data ?? EMPTY_SEGMENTS).filter((segment) =>
    segment.description.toLowerCase().includes(normalizedSearch)
  )

  const renderEmpty = () => {
    if (segmentsQuery.isPending) return <Loader size={24} />
    if (segmentsQuery.isError)
      return (
        <span>
          {getGeneralErrorMessage(segmentsQuery.error)}{' '}
          <Button type="link" onClick={() => segmentsQuery.refetch()}>
            Повторить
          </Button>
        </span>
      )
    return undefined
  }

  return (
    <div className={cn.segmentsTab}>
      {contextHolder}

      <div className={cn.toolbar}>
        <AtomSearchInput
          placeholder="Поиск по названию..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(value) => setSearch(value)}
          className={cn.searchInput}
          isOrganisationSelected={isOrgSelected}
        />
        <div className={cn.ownerSelect}>
          <HoverElement>
            <AtomSelect
              openByHover={false}
              outlined
              isOrganisationSelected={isOrgSelected}
              value={innerTab}
              options={SEGMENT_OWNER_OPTIONS}
              handleChange={(owner) => {
                if (owner === 'my' || owner === 'partners') setInnerTab(owner)
              }}
              popupClassName={
                isOrgSelected ? cn.ownerPopupSelected : cn.ownerPopup
              }
            />
          </HoverElement>
        </div>
        {innerTab === 'my' && canReorder && (
          <span className={cn.orderAction}>
            <AtomButton
              variant="secondary"
              icon={<OrderedListOutlined />}
              disabled={!segmentsQuery.data?.length}
              onClick={() => setIsOrderModalOpen(true)}
            >
              Изменить порядок
            </AtomButton>
            <Tooltip title={ORDER_HINT}>
              <QuestionCircleOutlined
                className={classNames(
                  cn.helpIcon,
                  isOrgSelected && cn.helpIconSelected
                )}
                aria-label="Зачем нужен порядок сегментов"
              />
            </Tooltip>
          </span>
        )}
        {innerTab === 'my' && (
          <AtomButton
            className={cn.createButton}
            icon={<PlusOutlined />}
            onClick={() => openModal({ type: 'create' })}
          >
            Создать сегмент
          </AtomButton>
        )}
      </div>

      <AtomTable
        ariaLabel={innerTab === 'my' ? 'Мои сегменты' : 'Сегменты партнёров'}
        data={segments}
        columns={columns}
        getRowId={(segment) => segment.id}
        columnWidths={SEGMENTS_COLUMN_WIDTHS}
        isOrganisationSelected={isOrgSelected}
        emptyContent={renderEmpty()}
      />

      {isOrderModalOpen && segmentsQuery.data && (
        <SegmentsOrderModal
          open
          segments={segmentsQuery.data}
          modal={modal}
          onClose={() => setIsOrderModalOpen(false)}
          onReload={async () => (await segmentsQuery.refetch()).data}
        />
      )}

      {modalMode && (
        <SegmentCreationModal
          key={modalKey}
          mode={modalMode}
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  )
}

export default SegmentsTab
